import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, Marker, Popup, Tooltip, useMap, useMapEvents, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { useApp } from '@/context/AppContext';
import { useMultipleMarkerSmoothing } from '@/hooks/useMarkerSmoothing';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { CachedTileLayer } from '@/components/map/CachedTileLayer';
import { getCacheStats, clearTileCache, formatBytes } from '@/lib/tileCache';
import { clearOfflineData } from '@/lib/offlineStore';
import { tripsAPI, pointsAPI, aisAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Users, MapPin, Target, Anchor, Ship, Waves, X, Move, Menu, Crown, Cloud, Navigation, Fuel, Crosshair, Bookmark, WifiOff, Trash2, HardDrive } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import SOSModal from './SOSModal';
import PremiumUpgradeModal from './PremiumUpgrade';
import WeatherWidget from './WeatherWidget';
import NavigationPanel from './NavigationPanel';
import MarinaPanel from './MarinaPanel';
import MOBMarkerModal from './MOBMarkerModal';
import SavedPointModal from './SavedPointModal';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.Default.css';

// Map layer configurations
const MAP_LAYERS = {
  // Ocean/Bathymetry base maps
  esriOcean: {
    name: 'ESRI Ocean (Depth)',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Sources: GEBCO, NOAA, CHS, OSU, UNH, CSUMB, National Geographic, DeLorme, NAVTEQ, and Esri',
    maxZoom: 16,
    maxNativeZoom: 13, // Tiles only go to 13, but we can overzoom
  },
  esriOceanReference: {
    name: 'Ocean Labels',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 16,
    maxNativeZoom: 13,
  },
  // OpenSeaMap nautical overlay
  openSeaMap: {
    name: 'OpenSeaMap (Nautical)',
    url: 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors',
    maxZoom: 18,
  },
  // Standard OSM for land areas
  osm: {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  // Satellite imagery
  esriSatellite: {
    name: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
  },
  // CartoDB Positron (for Marine style - desaturated land)
  cartoLight: {
    name: 'Carto Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
  },
  // CartoDB Dark (night mode)
  cartoDark: {
    name: 'Carto Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 20,
  },
  // NOAA nautical charts (US waters only)
  noaaCharts: {
    name: 'NOAA Charts (US)',
    url: 'https://tileservice.charts.noaa.gov/tiles/50000_1/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.nauticalcharts.noaa.gov/">NOAA</a>',
    maxZoom: 18,
    maxNativeZoom: 16,
  },
  // Great Lakes Bathymetry (depth shading for Great Lakes)
  greatLakesBathymetry: {
    name: 'Great Lakes Depth',
    url: 'https://edumaps.esri.ca/arcgis/rest/services/MapServices/GreatLakesBathymetry/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; ESRI Canada / NOAA',
    maxZoom: 18,
    maxNativeZoom: 15,
  },
  // OpenTopoMap (topographic)
  openTopo: {
    name: 'Topographic',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxZoom: 17,
  },
  // ESRI World Topo (better alternative)
  esriTopo: {
    name: 'Terrain',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19,
  },
  // ESRI NatGeo (beautiful style)
  esriNatGeo: {
    name: 'National Geographic',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; National Geographic, Esri, DeLorme, NAVTEQ',
    maxZoom: 18,
    maxNativeZoom: 16,
  },
};

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createBoatIcon = (color, isMe = false, heading = 0, faded = false) => {
  const size = isMe ? 44 : 32;
  return L.divIcon({
    className: 'custom-boat-marker',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border: 3px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
      opacity: ${faded ? 0.45 : 1};
      ${isMe ? 'animation: pulse 2s infinite;' : ''}
    ">
      <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white" style="transform: rotate(${heading}deg);">
        <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.34-.42-.6-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.48.26-.6.5s-.15.52-.06.78L3.95 19zM6 6h12v3.97L12 8 6 9.97V6z"/>
      </svg>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Simple small dot for anonymous public boaters
const createAnonymousDot = (isEmergency = false) => {
  const size = isEmergency ? 16 : 12;
  const color = isEmergency ? '#ef4444' : '#94a3b8'; // Red for SOS, gray otherwise
  return L.divIcon({
    className: 'anonymous-dot',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      ${isEmergency ? 'animation: pulse 1.5s infinite;' : ''}
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createReportIcon = (type) => {
  const color = type === 'HAZARD' ? '#ef4444' : '#f59e0b';
  const icon = type === 'HAZARD' ? '⚠️' : '📍';
  return L.divIcon({
    className: 'custom-report-marker',
    html: `<div style="
      width: 32px;
      height: 32px;
      background: ${color};
      border: 2px solid white;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${icon}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const createReportPinIcon = () => {
  return L.divIcon({
    className: 'report-pin-marker',
    html: `<div style="
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 20px;
        height: 20px;
        background: #ef4444;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 10px rgba(0,0,0,0.4);
        animation: bounce 0.5s ease infinite alternate;
      "></div>
    </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// SOS marker icon - red pulsing dot
const createSOSIcon = (isPrivate = false) => {
  const color = isPrivate ? '#06b6d4' : '#ef4444'; // cyan for private, red for public
  return L.divIcon({
    className: 'sos-marker',
    html: `<div style="
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 24px;
        height: 24px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 0 20px ${color}, 0 0 40px ${color};
        animation: sosPulse 1s ease-in-out infinite;
      "></div>
    </div>
    <style>
      @keyframes sosPulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.3); opacity: 0.7; }
      }
    </style>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// MOB (Man Overboard) marker icon - distinctive emergency marker
const createMOBIcon = (markerType = 'PERSON_OVERBOARD', isMine = false) => {
  const colors = {
    PERSON_OVERBOARD: '#dc2626', // Red
    DROPPED_ITEM: '#f59e0b', // Amber
    HAZARD: '#ef4444', // Red
    LAST_KNOWN: '#6366f1', // Indigo
  };
  const icons = {
    PERSON_OVERBOARD: '👤',
    DROPPED_ITEM: '📦',
    HAZARD: '⚠️',
    LAST_KNOWN: '📍',
  };
  const color = colors[markerType] || '#dc2626';
  const icon = icons[markerType] || '📍';
  const border = isMine ? '3px solid #22c55e' : '3px solid white';
  
  return L.divIcon({
    className: 'mob-marker',
    html: `<div style="
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 36px;
        height: 36px;
        background: ${color};
        border: ${border};
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 4px 12px ${color}80;
        animation: mobPulse 2s ease-in-out infinite;
      ">${icon}</div>
    </div>
    <style>
      @keyframes mobPulse {
        0%, 100% { transform: scale(1); box-shadow: 0 4px 12px ${color}80; }
        50% { transform: scale(1.05); box-shadow: 0 6px 20px ${color}; }
      }
    </style>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
};

// Saved Point (waypoint) marker icon
const createSavedPointIcon = (isMine = true) => {
  const color = isMine ? '#0891b2' : '#94a3b8'; // cyan-600 for mine, slate-400 for crew
  return L.divIcon({
    className: 'saved-point-marker',
    html: `<div style="
      width: 30px;
      height: 38px;
      position: relative;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    ">
      <svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 9 15 23 15 23s15-14 15-23C30 6.7 23.3 0 15 0z" fill="${color}"/>
        <circle cx="15" cy="15" r="11" fill="white"/>
      </svg>
      <div style="position:absolute; top:6px; left:0; width:30px; text-align:center; font-size:15px; line-height:18px;">🔖</div>
    </div>`,
    iconSize: [30, 38],
    iconAnchor: [15, 38],
    popupAnchor: [0, -34],
  });
};

// AIS vessel type -> color
const vesselColor = (label) => {
  switch (label) {
    case 'Cargo': return '#16a34a';
    case 'Tanker': return '#b45309';
    case 'Passenger': return '#2563eb';
    case 'Tug / Tow': return '#0d9488';
    case 'Fishing': return '#ea580c';
    case 'Sailing': return '#7c3aed';
    case 'Pleasure Craft': return '#db2777';
    case 'High Speed Craft': return '#0891b2';
    case 'Rescue / Law': return '#dc2626';
    default: return '#64748b';
  }
};

// AIS nav status code -> label
const navStatusLabel = (code) => {
  const m = {
    0: 'Under way (engine)', 1: 'At anchor', 2: 'Not under command',
    3: 'Restricted maneuver', 4: 'Constrained by draft', 5: 'Moored',
    6: 'Aground', 7: 'Fishing', 8: 'Under way (sailing)', 15: 'Undefined',
  };
  return m[code] || null;
};

// AIS vessel marker - directional arrow rotated to heading/course
const createVesselIcon = (heading, label, moving) => {
  const color = vesselColor(label);
  const rot = (heading === null || heading === undefined) ? 0 : heading;
  // Moving vessels: arrow shape. Stationary: rounded diamond.
  const shape = moving
    ? `<polygon points="10,1 17,19 10,14 3,19" fill="${color}" stroke="white" stroke-width="1.2"/>`
    : `<circle cx="10" cy="10" r="6" fill="${color}" stroke="white" stroke-width="1.5"/>`;
  return L.divIcon({
    className: 'ais-vessel-marker',
    html: `<div style="width:20px;height:20px;transform:rotate(${rot}deg);filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
      <svg width="20" height="20" viewBox="0 0 20 20">${shape}</svg>
    </div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// Boat photo marker - circular photo with a colored ring + heading arrow
const createPhotoBoatIcon = (photoUrl, ringColor, heading = 0, faded = false, isMe = false) => {
  const size = isMe ? 50 : 42;
  return L.divIcon({
    className: 'custom-boat-marker',
    html: `<div style="position:relative;width:${size}px;height:${size}px;opacity:${faded ? 0.45 : 1};">
      <div style="position:absolute;inset:0;transform:rotate(${heading}deg);">
        <div style="position:absolute;top:-7px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:9px solid ${ringColor};"></div>
      </div>
      <div style="position:absolute;inset:0;border-radius:50%;border:3px solid ${ringColor};overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.35);background:#e2e8f0;${isMe ? 'animation: pulse 2s infinite;' : ''}">
        <img src="${photoUrl}" style="width:100%;height:100%;object-fit:cover;display:block;" alt="" />
      </div>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Map recenter component
const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Auto-center on user location when it first becomes available
const AutoCenterOnLocation = ({ location, hasInitiallyCentered, setHasInitiallyCentered }) => {
  const map = useMap();
  
  useEffect(() => {
    // Only auto-center once when location first becomes available
    if (location?.lat && location?.lon && !hasInitiallyCentered) {
      map.flyTo([location.lat, location.lon], 14, {
        duration: 1.5
      });
      setHasInitiallyCentered(true);
    }
  }, [location?.lat, location?.lon, hasInitiallyCentered, setHasInitiallyCentered, map]);
  
  return null;
};

// Auto-follow mode - keeps map centered on user location
const AutoFollowLocation = ({ location, isEnabled, setAutoFollow }) => {
  const map = useMap();
  const lastLocationRef = React.useRef(null);
  
  // Disable auto-follow when user drags the map
  useMapEvents({
    dragstart: () => {
      if (isEnabled) {
        setAutoFollow(false);
      }
    },
  });
  
  useEffect(() => {
    if (location && isEnabled) {
      // Only pan if location actually changed
      const lastLoc = lastLocationRef.current;
      if (!lastLoc || lastLoc.lat !== location.lat || lastLoc.lon !== location.lon) {
        lastLocationRef.current = { lat: location.lat, lon: location.lon };
        map.panTo([location.lat, location.lon], {
          animate: true,
          duration: 0.5
        });
      }
    }
  }, [location?.lat, location?.lon, isEnabled, map]);
  
  return null;
};

// Report pin placement component
const ReportPinPlacer = ({ isActive, position, setPosition, mapCenter }) => {
  const map = useMap();
  
  useMapEvents({
    moveend: () => {
      if (isActive) {
        const center = map.getCenter();
        setPosition([center.lat, center.lng]);
      }
    },
  });

  useEffect(() => {
    if (isActive && !position) {
      const center = map.getCenter();
      setPosition([center.lat, center.lng]);
    }
  }, [isActive, position, setPosition, map]);

  if (!isActive || !position) return null;

  return (
    <Marker position={position} icon={createReportPinIcon()} />
  );
};

// Long-press handler to drop a saved point at the tapped location
const SavedPointPlacer = ({ enabled, onDropPoint }) => {
  useMapEvents({
    contextmenu: (e) => {
      if (enabled) {
        onDropPoint({ lat: e.latlng.lat, lon: e.latlng.lng });
      }
    },
  });
  return null;
};

// Live AIS vessel traffic layer - polls backend for vessels in the current viewport
const VesselTrafficLayer = ({ enabled, onUnavailable }) => {
  const map = useMap();
  const [vessels, setVessels] = useState([]);
  const intervalRef = useRef(null);

  const fetchVessels = useCallback(async () => {
    if (!enabled) return;
    try {
      const b = map.getBounds();
      const zoom = map.getZoom();
      // AIS is dense; only fetch when reasonably zoomed in
      if (zoom < 8) {
        setVessels([]);
        onUnavailable && onUnavailable('zoom');
        return;
      }
      const { data } = await aisAPI.getVessels(
        b.getSouth(), b.getWest(), b.getNorth(), b.getEast()
      );
      if (!data.available) {
        setVessels([]);
        onUnavailable && onUnavailable('key');
        return;
      }
      onUnavailable && onUnavailable(data.connected ? null : 'connecting');
      setVessels(data.vessels || []);
    } catch (e) {
      // silent - degrade gracefully
    }
  }, [enabled, map, onUnavailable]);

  // Refetch when the map stops moving
  useMapEvents({
    moveend: () => { if (enabled) fetchVessels(); },
  });

  useEffect(() => {
    if (!enabled) {
      setVessels([]);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    fetchVessels();
    intervalRef.current = setInterval(fetchVessels, 15000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [enabled, fetchVessels]);

  if (!enabled) return null;

  return (
    <>
      {vessels.map((v) => {
        const moving = (v.sog || 0) > 0.5;
        const dir = (v.heading ?? v.cog ?? 0);
        const navLabel = navStatusLabel(v.nav_status);
        return (
          <Marker
            key={`ais-${v.mmsi}`}
            position={[v.lat, v.lon]}
            icon={createVesselIcon(dir, v.ship_type_label, moving)}
          >
            <Popup>
              <div className="min-w-[170px]">
                <p className="font-bold text-gray-800 flex items-center gap-1">
                  <Ship className="w-3.5 h-3.5" style={{ color: vesselColor(v.ship_type_label) }} />
                  {v.name}
                </p>
                <p className="text-xs font-medium" style={{ color: vesselColor(v.ship_type_label) }}>
                  {v.ship_type_label}
                </p>
                <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                  <p>Speed: {v.sog != null ? `${v.sog.toFixed(1)} kt` : 'n/a'}</p>
                  <p>Course: {v.cog != null ? `${Math.round(v.cog)}°` : 'n/a'}</p>
                  {navLabel && <p>Status: {navLabel}</p>}
                  {v.destination && <p className="truncate">Dest: {v.destination}</p>}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">MMSI {v.mmsi}</p>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

// Motion state helper with icons
const getMotionState = (speed) => {
  if (speed > 5) return { label: 'Underway', color: 'bg-green-500', textColor: 'text-green-600', icon: '🟢', emoji: '🚤' };
  if (speed > 0.5) return { label: 'Drifting', color: 'bg-yellow-500', textColor: 'text-yellow-600', icon: '🟡', emoji: '🌊' };
  return { label: 'Anchored', color: 'bg-blue-500', textColor: 'text-blue-600', icon: '⚓', emoji: '⚓' };
};

// Storage keys
const STORAGE_KEYS = {
  mapStyle: 'boat360_mapStyle',
  showBathymetry: 'boat360_showBathymetry',
  showSeamarks: 'boat360_showSeamarks',
  showGreatLakes: 'boat360_showGreatLakes',
  showTerrain: 'boat360_showTerrain',
  showVessels: 'boat360_showVessels',
};

const MapScreen = ({ onReportClick }) => {
  const {
    myLocation,
    publicBoaters,
    friendsLocations,
    nearbyReports,
    activeTrip,
    user,
    voteOnReport,
    setMapCenter,
    focusLocation,
    setFocusLocation,
    createReport,
    realtimeConnected,
    updateSubscription,
    // Subscription & SOS (Boat 360 Freemium)
    isPaid,
    subscription,
    upgradeSubscription,
    activeSOS,
    nearbyPublicSOS,
    friendsSOS,
    triggerSOS,
    cancelSOS,
    acknowledgeSOS,
    // Emergency Markers (MOB)
    emergencyMarkers,
    createEmergencyMarker,
    clearEmergencyMarker,
    friends,
  } = useApp();

  // Load saved preferences
  const [showPublic, setShowPublic] = useState(true);
  const [showFriends, setShowFriends] = useState(true);
  const [showReports, setShowReports] = useState(true);
  const [showSeamarks, setShowSeamarks] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.showSeamarks);
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showBathymetry, setShowBathymetry] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.showBathymetry);
    return saved !== null ? JSON.parse(saved) : false; // OFF by default
  });
  const [showGreatLakes, setShowGreatLakes] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.showGreatLakes);
    return saved !== null ? JSON.parse(saved) : false; // OFF by default
  });
  const [showVessels, setShowVessels] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.showVessels);
    return saved !== null ? JSON.parse(saved) : false; // OFF by default
  });
  const [aisState, setAisState] = useState(null); // null=ok, 'key'|'connecting'|'zoom'
  const isOnline = useOnlineStatus();
  const [cacheStats, setCacheStats] = useState({ count: 0, bytes: 0 });
  const [clearingCache, setClearingCache] = useState(false);
  const [mapStyle, setMapStyle] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.mapStyle) || 'standard';
  });
  
  // Report mode state
  const [reportMode, setReportMode] = useState(false);
  const [reportPinPosition, setReportPinPosition] = useState(null);
  
  const [recenterTrigger, setRecenterTrigger] = useState(null);
  const [hasInitiallyCentered, setHasInitiallyCentered] = useState(false);
  const mapRef = useRef(null);

  // SOS Modal state
  const [showSOSModal, setShowSOSModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeature, setPremiumFeature] = useState(null);
  const [sosLoading, setSOSLoading] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showMarinas, setShowMarinas] = useState(false);
  const [navDestination, setNavDestination] = useState(null);
  
  // MOB (Man Overboard) Modal state
  const [showMOBModal, setShowMOBModal] = useState(false);
  
  // Saved Points (Waypoints) state - Boat 360 Plus
  const [savedPoints, setSavedPoints] = useState([]);
  const [showPointModal, setShowPointModal] = useState(false);
  const [pendingPointLatLng, setPendingPointLatLng] = useState(null);
  
  // First-time user onboarding
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const seen = localStorage.getItem('boat360_onboarding_seen');
    return !seen;
  });
  
  // Auto-follow mode - keeps map centered on user
  const [autoFollow, setAutoFollow] = useState(false);
  
  // Route tracking during active trip
  const [routePoints, setRoutePoints] = useState([]);
  
  const dismissOnboarding = () => {
    localStorage.setItem('boat360_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  // Fetch route points during active trip
  useEffect(() => {
    const fetchRoutePoints = async () => {
      if (activeTrip?.id) {
        try {
          const response = await tripsAPI.getRoute(activeTrip.id);
          if (response.data?.points) {
            setRoutePoints(response.data.points.map(p => [p.lat, p.lon]));
          }
        } catch (error) {
          console.error('Failed to fetch route:', error);
        }
      } else {
        setRoutePoints([]);
      }
    };
    
    fetchRoutePoints();
    // Refresh route every 30 seconds during active trip
    const interval = activeTrip ? setInterval(fetchRoutePoints, 30000) : null;
    return () => interval && clearInterval(interval);
  }, [activeTrip?.id]);

  // Fetch saved points (Boat 360 Plus only)
  const fetchSavedPoints = useCallback(async () => {
    if (!isPaid) {
      setSavedPoints([]);
      return;
    }
    try {
      const response = await pointsAPI.getMine();
      setSavedPoints(response.data || []);
    } catch (error) {
      console.error('Failed to fetch saved points:', error);
    }
  }, [isPaid]);

  useEffect(() => {
    fetchSavedPoints();
  }, [fetchSavedPoints]);

  // Drop a point via long-press on the map (Plus gated)
  const handleDropPoint = (latlng) => {
    if (!isPaid) {
      setShowPremiumModal(true);
      return;
    }
    setPendingPointLatLng(latlng);
    setShowPointModal(true);
  };

  // Open the saved points manager (from the floating button)
  const handleOpenPoints = () => {
    if (!isPaid) {
      setShowPremiumModal(true);
      return;
    }
    setPendingPointLatLng(null);
    setShowPointModal(true);
  };

  const handleCreatePoint = async (data) => {
    try {
      await pointsAPI.create(data);
      toast.success('Point saved');
      setPendingPointLatLng(null);
      await fetchSavedPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save point');
      throw error;
    }
  };

  const handleDeletePoint = async (pointId) => {
    try {
      await pointsAPI.delete(pointId);
      toast.success('Point deleted');
      await fetchSavedPoints();
    } catch (error) {
      toast.error('Failed to delete point');
    }
  };

  const handleSharePoint = async (pointId, friendUserId) => {
    try {
      const res = await pointsAPI.share(pointId, friendUserId);
      toast.success(res.data?.message || 'Point shared');
      await fetchSavedPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share point');
    }
  };

  const handleNavigatePoint = (point) => {
    setNavDestination({ lat: point.lat, lon: point.lon, name: point.name });
    setShowNavigation(true);
    setShowPointModal(false);
  };

  // Marker smoothing for friends
  const smoothedFriendPositions = useMultipleMarkerSmoothing(friendsLocations);

  // Memoized cluster icon creator for public boaters
  const createClusterIcon = useCallback((cluster) => {
    const count = cluster.getChildCount();
    let size = 'small';
    let className = 'custom-cluster-small';
    
    if (count >= 100) {
      size = 'large';
      className = 'custom-cluster-large';
    } else if (count >= 10) {
      size = 'medium';
      className = 'custom-cluster-medium';
    }
    
    return L.divIcon({
      html: `<div class="${className}"><span>${count}</span></div>`,
      className: 'custom-cluster-icon',
      iconSize: L.point(40, 40, true),
    });
  }, []);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.mapStyle, mapStyle);
  }, [mapStyle]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showBathymetry, JSON.stringify(showBathymetry));
  }, [showBathymetry]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showSeamarks, JSON.stringify(showSeamarks));
  }, [showSeamarks]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showGreatLakes, JSON.stringify(showGreatLakes));
  }, [showGreatLakes]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.showVessels, JSON.stringify(showVessels));
  }, [showVessels]);

  // Offline cache stats
  const refreshCacheStats = useCallback(async () => {
    try {
      const stats = await getCacheStats();
      setCacheStats(stats);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    refreshCacheStats();
    const interval = setInterval(refreshCacheStats, 20000);
    return () => clearInterval(interval);
  }, [refreshCacheStats]);

  const handleClearCache = useCallback(async () => {
    setClearingCache(true);
    try {
      await clearTileCache();
      await clearOfflineData();
      await refreshCacheStats();
      toast.success('Offline cache cleared');
    } catch (e) {
      toast.error('Could not clear cache');
    } finally {
      setClearingCache(false);
    }
  }, [refreshCacheStats]);

  // "Locate" a crew member: fly the map to the requested position (one-shot)
  useEffect(() => {
    if (focusLocation && focusLocation.lat != null && focusLocation.lon != null) {
      setHasInitiallyCentered(true); // prevent auto-center-on-me from overriding
      setRecenterTrigger([focusLocation.lat, focusLocation.lon]);
      const t = setTimeout(() => setFocusLocation(null), 1200);
      return () => clearTimeout(t);
    }
  }, [focusLocation, setFocusLocation]);

  // Boat photo cache: fetch each crew/public boater photo once (authenticated),
  // store as an object URL keyed by user. Re-fetches only when the photo changes.
  const [boatPhotos, setBoatPhotos] = useState({}); // userId -> objectURL
  const boatPhotoMeta = useRef({}); // userId -> boat_photo_updated_at

  useEffect(() => {
    const targets = [];
    friendsLocations.forEach((f) => {
      if (f.has_boat_photo) targets.push([f.friend_user_id, f.boat_photo_updated_at]);
    });
    publicBoaters.forEach((b) => {
      if (b.has_boat_photo && b.user_id) targets.push([b.user_id, b.boat_photo_updated_at]);
    });
    targets.forEach(([id, updated]) => {
      if (boatPhotoMeta.current[id] === (updated || '')) return; // already have latest
      boatPhotoMeta.current[id] = updated || '';
      api.get(`/boat-photo/${id}`, { responseType: 'blob' })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          setBoatPhotos((prev) => {
            const old = prev[id];
            if (old && old.startsWith('blob:')) { try { URL.revokeObjectURL(old); } catch (e) {} }
            return { ...prev, [id]: url };
          });
        })
        .catch(() => { boatPhotoMeta.current[id] = null; });
    });
  }, [friendsLocations, publicBoaters]);

  const handleCenterOnMe = () => {
    if (myLocation) {
      if (autoFollow) {
        // If already in auto-follow, disable it
        setAutoFollow(false);
      } else {
        // Center on location and enable auto-follow
        setRecenterTrigger([myLocation.lat, myLocation.lon]);
        setAutoFollow(true);
      }
    }
  };

  const handleReportModeToggle = () => {
    if (reportMode) {
      // Cancel report mode
      setReportMode(false);
      setReportPinPosition(null);
    } else {
      // Enter report mode
      setReportMode(true);
      // Pin will be set by ReportPinPlacer component
    }
  };

  const handleConfirmReport = () => {
    if (reportPinPosition && onReportClick) {
      // Pass the pin position to the report screen
      onReportClick(reportPinPosition);
      setReportMode(false);
      setReportPinPosition(null);
    }
  };

  // Controls visibility state
  const [showControls, setShowControls] = useState(false);

  // True when any panel/modal is open — used to hide the floating action
  // buttons so they don't overlap/block the open window.
  const panelOpen = showNavigation || showWeather || showMOBModal || showSOSModal || showPointModal || showPremiumModal;

  // Handle SOS trigger from modal
  const handleTriggerSOS = async (distressType, sosMode) => {
    setSOSLoading(true);
    try {
      await triggerSOS(distressType, sosMode);
    } finally {
      setSOSLoading(false);
    }
  };

  // Handle premium upgrade
  const handleUpgrade = async () => {
    try {
      await upgradeSubscription();
      setShowPremiumModal(false);
    } catch (error) {
      // Error handled in context
    }
  };

  const myMotion = myLocation ? getMotionState(myLocation.speed) : null;

  // Map style CSS filter for Marine mode
  const getMapFilter = () => {
    // No filter needed - Marine now uses ESRI Ocean base which has proper colors
    return 'none';
  };

  // Check if user has active SOS
  const hasActiveSOS = activeSOS?.public_sos || activeSOS?.private_sos;

  return (
    <div className="h-full flex flex-col relative" data-testid="map-screen">
      {/* SOS Modal */}
      <SOSModal 
        isOpen={showSOSModal}
        onClose={() => setShowSOSModal(false)}
        onTriggerSOS={handleTriggerSOS}
        onCancelSOS={cancelSOS}
        isPaid={isPaid}
        isLoading={sosLoading}
        activeSOS={activeSOS}
      />

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showPremiumModal}
        onClose={() => { setShowPremiumModal(false); setPremiumFeature(null); }}
        onUpgrade={handleUpgrade}
        blockedFeature={premiumFeature}
      />

      {/* MOB (Man Overboard) Marker Modal */}
      <MOBMarkerModal 
        isOpen={showMOBModal}
        onClose={() => setShowMOBModal(false)}
        onCreateMarker={createEmergencyMarker}
        myLocation={myLocation}
        emergencyMarkers={emergencyMarkers}
        onClearMarker={clearEmergencyMarker}
      />

      {/* Saved Points (Waypoints) Modal */}
      <SavedPointModal
        isOpen={showPointModal}
        onClose={() => { setShowPointModal(false); setPendingPointLatLng(null); }}
        onCreatePoint={handleCreatePoint}
        onDeletePoint={handleDeletePoint}
        onSharePoint={handleSharePoint}
        onNavigatePoint={handleNavigatePoint}
        savedPoints={savedPoints}
        friends={friends || []}
        pendingLatLng={pendingPointLatLng}
        myLocation={myLocation}
      />

      {/* Weather Widget */}
      {showWeather && myLocation && (
        <WeatherWidget 
          lat={myLocation.lat} 
          lon={myLocation.lon}
          onClose={() => setShowWeather(false)}
        />
      )}

      {/* Weather Toggle Button - if hidden */}
      {!showWeather && !panelOpen && (
        <button
          onClick={() => setShowWeather(true)}
          className="absolute left-4 z-[1000] w-10 h-10 bg-white/95 backdrop-blur shadow-lg rounded-full flex items-center justify-center hover:bg-white transition-colors"
          style={{ top: 'calc(5rem + env(safe-area-inset-top))' }}
          data-testid="show-weather-btn"
          title="Show weather"
        >
          <Cloud className="w-5 h-5 text-cyan-600" />
        </button>
      )}

      {/* Menu Toggle Button - Always visible */}
      {!panelOpen && (
      <button
        onClick={() => setShowControls(!showControls)}
        onTouchStart={(e) => { e.stopPropagation(); }}
        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowControls(!showControls); }}
        className="absolute left-3 z-[2000] w-10 h-10 bg-white/95 backdrop-blur shadow-lg rounded-lg flex items-center justify-center cursor-pointer active:bg-gray-100"
        style={{ top: 'calc(0.75rem + env(safe-area-inset-top))', touchAction: 'manipulation', pointerEvents: 'auto', WebkitTapHighlightColor: 'transparent' }}
        data-testid="toggle-controls"
      >
        {showControls ? (
          <X className="w-5 h-5 text-gray-600" />
        ) : (
          <Menu className="w-5 h-5 text-gray-600" />
        )}
      </button>
      )}

      {/* Upgrade Badge for Free Users - Always visible next to menu */}
      {!isPaid && !showControls && !panelOpen && (
        <button
          onClick={() => setShowPremiumModal(true)}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowPremiumModal(true); }}
          className="absolute left-14 z-[2000] px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-medium rounded-lg shadow-lg flex items-center gap-1.5 active:from-cyan-600 active:to-blue-600"
          style={{ top: 'calc(0.75rem + env(safe-area-inset-top))', touchAction: 'manipulation', pointerEvents: 'auto', WebkitTapHighlightColor: 'transparent' }}
          data-testid="upgrade-badge"
        >
          <Crown className="w-3.5 h-3.5" />
          <span>Get Plus</span>
        </button>
      )}

      {/* SOS Active Banner - Shows when SOS is active */}
      {hasActiveSOS && (
        <div
          className="absolute left-3 right-3 z-[1002] animate-pulse"
          style={{ top: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          <div className="bg-red-600 text-white text-center py-2 px-4 rounded-lg shadow-lg flex items-center justify-center gap-2">
            <span className="text-lg">🆘</span>
            <span className="font-bold text-sm">SOS ACTIVE</span>
            <span className="text-lg">🆘</span>
          </div>
        </div>
      )}

      {/* SOS Button - Always visible, top right */}
      {!panelOpen && (
      <div
        className="absolute right-3 z-[2000] flex flex-col gap-2"
        style={{ top: 'calc(0.75rem + env(safe-area-inset-top))', pointerEvents: 'auto' }}
      >
        <button
          onClick={() => setShowSOSModal(true)}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowSOSModal(true); }}
          className={`w-14 h-14 ${hasActiveSOS ? 'bg-orange-500 animate-pulse' : 'bg-red-500 hover:bg-red-600 active:bg-red-700'} shadow-lg rounded-full flex items-center justify-center cursor-pointer`}
          style={{ touchAction: 'manipulation', pointerEvents: 'auto', WebkitTapHighlightColor: 'transparent' }}
          data-testid="sos-btn"
        >
          <span className="text-white font-bold text-sm">SOS</span>
        </button>
        
        {/* MOB (Man Overboard) Button */}
        <button
          onClick={() => setShowMOBModal(true)}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowMOBModal(true); }}
          className={`w-14 h-14 ${emergencyMarkers?.length > 0 ? 'bg-amber-500 ring-2 ring-amber-300' : 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800'} shadow-lg rounded-full flex items-center justify-center cursor-pointer`}
          style={{ touchAction: 'manipulation', pointerEvents: 'auto', WebkitTapHighlightColor: 'transparent' }}
          data-testid="mob-btn"
          title="Mark Emergency Location (MOB)"
        >
          <Crosshair className="w-6 h-6 text-white" />
        </button>

        {/* Saved Points Button */}
        <button
          onClick={handleOpenPoints}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenPoints(); }}
          className={`w-14 h-14 ${savedPoints.length > 0 ? 'bg-cyan-500 ring-2 ring-cyan-300' : 'bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800'} shadow-lg rounded-full flex items-center justify-center cursor-pointer`}
          style={{ touchAction: 'manipulation', pointerEvents: 'auto', WebkitTapHighlightColor: 'transparent' }}
          data-testid="saved-points-btn"
          title="Saved Points"
        >
          <Bookmark className="w-6 h-6 text-white" />
        </button>
      </div>
      )}

      {/* Map Controls - Collapsible */}
      {showControls && (
        <div
          className="absolute left-3 right-20 z-[1000] animate-in slide-in-from-top duration-200"
          style={{ top: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          {/* Upgrade Banner for Free Users - Inside Menu */}
          {!isPaid && (
            <button
              onClick={() => { setShowPremiumModal(true); setShowControls(false); }}
              className="w-full mb-2 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg hover:from-cyan-600 hover:to-blue-600 transition-all"
              data-testid="premium-badge"
            >
              <Crown className="w-4 h-4" />
              <span>Upgrade to Boat 360 Plus</span>
            </button>
          )}
          
          {/* Layer Toggles - Compact Row */}
          <Card className="bg-white/95 backdrop-blur shadow-lg mb-2">
            <CardContent className="p-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <Switch
                    id="show-public"
                    checked={showPublic}
                    onCheckedChange={setShowPublic}
                    data-testid="toggle-public"
                    className="scale-90"
                  />
                  <Label htmlFor="show-public" className="text-xs flex items-center gap-0.5">
                    <Users className="w-3 h-3 text-gray-500" /> 
                    <span className="hidden sm:inline">Public</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    id="show-friends"
                    checked={showFriends}
                    onCheckedChange={setShowFriends}
                    data-testid="toggle-friends"
                    className="scale-90"
                  />
                  <Label htmlFor="show-friends" className="text-xs flex items-center gap-0.5">
                    <Ship className="w-3 h-3 text-cyan-500" /> 
                    <span className="hidden sm:inline">Crew</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    id="show-reports"
                    checked={showReports}
                    onCheckedChange={setShowReports}
                    data-testid="toggle-reports"
                    className="scale-90"
                  />
                  <Label htmlFor="show-reports" className="text-xs flex items-center gap-0.5">
                    <AlertTriangle className="w-3 h-3 text-orange-500" /> 
                    <span className="hidden sm:inline">Hazards</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    id="show-marinas"
                    checked={showMarinas}
                    onCheckedChange={setShowMarinas}
                    data-testid="toggle-marinas"
                    className="scale-90"
                  />
                  <Label htmlFor="show-marinas" className="text-xs flex items-center gap-0.5">
                    <Fuel className="w-3 h-3 text-red-500" /> 
                    <span className="hidden sm:inline">Services</span>
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Map Style & Depth - Second Row */}
          <Card className="bg-white/95 backdrop-blur shadow-lg">
            <CardContent className="p-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={mapStyle}
                  onChange={(e) => setMapStyle(e.target.value)}
                  className="text-xs border rounded px-1.5 py-1 bg-white flex-shrink-0"
                  data-testid="map-style-select"
                >
                  <optgroup label="Base Maps">
                    <option value="standard">🗺️ Standard</option>
                    <option value="satellite">🛰️ Satellite</option>
                    <option value="marine">🌊 Marine</option>
                    <option value="terrain">⛰️ Terrain</option>
                    <option value="natgeo">🌍 Nat Geo</option>
                    <option value="dark">🌙 Dark</option>
                  </optgroup>
                  <optgroup label="Nautical Charts">
                    <option value="noaa">📍 NOAA (US)</option>
                  </optgroup>
                </select>
                <div className="flex items-center gap-1" title="Ocean depth shading (worldwide)">
                  <Switch
                    id="show-bathymetry"
                    checked={showBathymetry}
                    onCheckedChange={setShowBathymetry}
                    data-testid="toggle-bathymetry"
                    className="scale-90"
                  />
                  <Label htmlFor="show-bathymetry" className="text-[10px] flex items-center gap-0.5 cursor-help">
                    <Waves className="w-3 h-3 text-blue-500" />
                    <span>Depth</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1" title="OpenSeaMap buoys and markers">
                  <Switch
                    id="show-seamark"
                    checked={showSeamarks}
                    onCheckedChange={setShowSeamarks}
                    data-testid="toggle-seamark"
                    className="scale-90"
                  />
                  <Label htmlFor="show-seamark" className="text-xs flex items-center gap-0.5">
                    <Anchor className="w-3 h-3 text-blue-600" />
                    <span className="hidden sm:inline">Marks</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1" title="Live vessel traffic (AIS)">
                  <Switch
                    id="show-vessels"
                    checked={showVessels}
                    onCheckedChange={setShowVessels}
                    data-testid="toggle-vessels"
                    className="scale-90"
                  />
                  <Label htmlFor="show-vessels" className="text-xs flex items-center gap-0.5">
                    <Ship className="w-3 h-3 text-green-600" />
                    <span className="hidden sm:inline">Traffic</span>
                  </Label>
                </div>
              </div>
              {/* Offline cache row */}
              <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-1 text-[11px] text-gray-600" title="Maps you view are saved automatically for offline use">
                  <HardDrive className="w-3.5 h-3.5 text-cyan-600" />
                  <span data-testid="offline-cache-size">
                    Offline maps: {formatBytes(cacheStats.bytes)}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClearCache}
                  disabled={clearingCache || cacheStats.count === 0}
                  data-testid="clear-cache-btn"
                  className="h-6 px-2 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  {clearingCache ? 'Clearing…' : 'Clear'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Offline indicator - shows when device has no connectivity */}
      {!isOnline && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1100]" data-testid="offline-indicator">
          <div className="bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
            <WifiOff className="w-3.5 h-3.5" />
            <span>Offline — using saved maps &amp; data</span>
          </div>
        </div>
      )}

      {/* AIS Vessel Traffic status banner */}
      {showVessels && aisState && (
        <div className="absolute top-32 left-1/2 -translate-x-1/2 z-[1000]" data-testid="ais-status-banner">
          <div className="bg-slate-800/90 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
            <Ship className="w-3.5 h-3.5" />
            {aisState === 'zoom' && <span>Zoom in to see live vessel traffic</span>}
            {aisState === 'key' && <span>Live vessel traffic coming soon</span>}
            {aisState === 'connecting' && <span>Connecting to vessel traffic…</span>}
          </div>
        </div>
      )}

      {showBathymetry && (
        <div className="absolute bottom-36 sm:bottom-32 left-2 z-[1000]">
          <Card className="bg-white/95 backdrop-blur shadow-lg max-w-[180px] sm:max-w-[220px]">
            <CardContent className="p-2 sm:p-2.5">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                <Waves className="w-3 h-3" /> Depth
              </p>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-[9px] sm:text-[10px] text-gray-500">6ft</span>
                <div className="flex h-2 rounded overflow-hidden flex-1">
                  <div className="flex-1" style={{ background: '#b3e0ff' }} />
                  <div className="flex-1" style={{ background: '#7fc4f5' }} />
                  <div className="flex-1" style={{ background: '#4a9ed9' }} />
                  <div className="flex-1" style={{ background: '#1a4d80' }} />
                </div>
                <span className="text-[9px] sm:text-[10px] text-gray-500">60ft+</span>
              </div>
              {/* Professional Disclaimer */}
              <p className="text-[8px] sm:text-[9px] text-gray-400 mt-1.5 leading-tight border-t border-gray-200 pt-1.5">
                <strong>Notice:</strong> Depth data is approximate. Not for navigation. 
                Use proper charts and equipment.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Mode Overlay */}
      {reportMode && (
        <>
          {/* Crosshair in center */}
          <div className="absolute inset-0 z-[999] pointer-events-none flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-2 border-red-500 rounded-full flex items-center justify-center bg-white/50">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
              </div>
              <div className="mt-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <Move className="w-3 h-3" /> Drag map to position
              </div>
            </div>
          </div>
          
          {/* Report Mode Controls */}
          <div className="absolute bottom-36 left-4 right-4 z-[1000]">
            <Card className="bg-white shadow-lg border-red-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">📍 Place Hazard Pin</p>
                    <p className="text-sm text-gray-500">Move map to position the pin</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleReportModeToggle}
                      className="h-9"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleConfirmReport}
                      className="h-9 bg-red-500 hover:bg-red-600"
                      data-testid="confirm-report-pin"
                    >
                      Confirm Location
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Map */}
      <div className="flex-1" style={{ filter: getMapFilter() }}>
        <MapContainer
          center={focusLocation ? [focusLocation.lat, focusLocation.lon] : (myLocation ? [myLocation.lat, myLocation.lon] : [25.7617, -80.1918])}
          zoom={13}
          maxZoom={19}
          zoomControl={false}
          className="h-full w-full"
          ref={mapRef}
        >
          {/* Base Layers based on style */}
          {mapStyle === 'standard' && (
            <CachedTileLayer
              attribution={MAP_LAYERS.osm.attribution}
              url={MAP_LAYERS.osm.url}
              maxZoom={MAP_LAYERS.osm.maxZoom}
            />
          )}
          
          {mapStyle === 'satellite' && (
            <CachedTileLayer
              attribution={MAP_LAYERS.esriSatellite.attribution}
              url={MAP_LAYERS.esriSatellite.url}
              maxZoom={MAP_LAYERS.esriSatellite.maxZoom}
            />
          )}
          
          {mapStyle === 'marine' && (
            <>
              {/* Ocean base with bathymetry - shows water depth nicely */}
              <CachedTileLayer
                attribution={MAP_LAYERS.esriOcean.attribution}
                url={MAP_LAYERS.esriOcean.url}
                maxZoom={MAP_LAYERS.esriOcean.maxZoom}
                maxNativeZoom={MAP_LAYERS.esriOcean.maxNativeZoom}
              />
              {/* OpenSeaMap overlay for buoys, channels, and marine features */}
              <CachedTileLayer
                attribution={MAP_LAYERS.openSeaMap.attribution}
                url={MAP_LAYERS.openSeaMap.url}
                maxZoom={MAP_LAYERS.openSeaMap.maxZoom}
                opacity={0.9}
              />
            </>
          )}

          {mapStyle === 'terrain' && (
            <CachedTileLayer
              attribution={MAP_LAYERS.esriTopo.attribution}
              url={MAP_LAYERS.esriTopo.url}
              maxZoom={MAP_LAYERS.esriTopo.maxZoom}
            />
          )}

          {mapStyle === 'dark' && (
            <CachedTileLayer
              attribution={MAP_LAYERS.cartoDark.attribution}
              url={MAP_LAYERS.cartoDark.url}
              maxZoom={MAP_LAYERS.cartoDark.maxZoom}
            />
          )}

          {mapStyle === 'natgeo' && (
            <CachedTileLayer
              attribution={MAP_LAYERS.esriNatGeo.attribution}
              url={MAP_LAYERS.esriNatGeo.url}
              maxZoom={MAP_LAYERS.esriNatGeo.maxZoom}
              maxNativeZoom={MAP_LAYERS.esriNatGeo.maxNativeZoom}
            />
          )}
          
          {mapStyle === 'noaa' && (
            <>
              <CachedTileLayer
                attribution={MAP_LAYERS.esriOcean.attribution}
                url={MAP_LAYERS.esriOcean.url}
                maxZoom={MAP_LAYERS.esriOcean.maxZoom}
                maxNativeZoom={MAP_LAYERS.esriOcean.maxNativeZoom}
              />
              <CachedTileLayer
                attribution={MAP_LAYERS.noaaCharts.attribution}
                url={MAP_LAYERS.noaaCharts.url}
                maxZoom={MAP_LAYERS.noaaCharts.maxZoom}
                maxNativeZoom={MAP_LAYERS.noaaCharts.maxNativeZoom}
                opacity={0.8}
              />
            </>
          )}

          {/* Ocean Bathymetry overlay (worldwide) - works on all map styles except those with built-in ocean */}
          {showBathymetry && (
            <CachedTileLayer
              attribution={MAP_LAYERS.esriOcean.attribution}
              url={MAP_LAYERS.esriOcean.url}
              maxZoom={MAP_LAYERS.esriOcean.maxZoom}
              maxNativeZoom={MAP_LAYERS.esriOcean.maxNativeZoom}
              opacity={mapStyle === 'satellite' ? 0.6 : 0.7}
            />
          )}

          {/* OpenSeaMap nautical overlay */}
          {showSeamarks && (
            <CachedTileLayer
              attribution={MAP_LAYERS.openSeaMap.attribution}
              url={MAP_LAYERS.openSeaMap.url}
              maxZoom={MAP_LAYERS.openSeaMap.maxZoom}
            />
          )}

          {/* Live AIS vessel traffic (free) */}
          <VesselTrafficLayer enabled={showVessels && !reportMode} onUnavailable={setAisState} />

          {/* Auto-center on user's GPS location when first available */}
          <AutoCenterOnLocation 
            location={myLocation}
            hasInitiallyCentered={hasInitiallyCentered}
            setHasInitiallyCentered={setHasInitiallyCentered}
          />

          {/* Auto-follow mode - keeps map centered on user */}
          <AutoFollowLocation 
            location={myLocation}
            isEnabled={autoFollow}
            setAutoFollow={setAutoFollow}
          />

          {recenterTrigger && <RecenterMap center={recenterTrigger} />}
          
          {/* Report pin placer */}
          <ReportPinPlacer 
            isActive={reportMode} 
            position={reportPinPosition} 
            setPosition={setReportPinPosition}
          />

          {/* Route tracking line during active trip */}
          {activeTrip && routePoints.length > 1 && (
            <Polyline
              positions={routePoints}
              pathOptions={{
                color: '#0ea5e9',
                weight: 4,
                opacity: 0.7,
                dashArray: null,
                lineCap: 'round',
                lineJoin: 'round'
              }}
            />
          )}

          {/* My location */}
          {myLocation && !reportMode && (() => {
            const hasActiveSOS = !!(activeSOS?.public_sos || activeSOS?.private_sos);
            const markerColor = hasActiveSOS ? '#ef4444' : '#0ea5e9';
            return (
              <Marker
                key={`my-location-${hasActiveSOS ? 'sos' : 'normal'}-${myLocation.lat}-${myLocation.lon}`}
                position={[myLocation.lat, myLocation.lon]}
                icon={user?.boat_photo_url
                  ? createPhotoBoatIcon(user.boat_photo_url, markerColor, myLocation.heading, false, true)
                  : createBoatIcon(markerColor, true, myLocation.heading)}
              >
                <Popup>
                  <div className="text-center">
                    {hasActiveSOS && (
                      <div className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded mb-2">
                        🆘 SOS ACTIVE
                      </div>
                    )}
                    <p className="font-bold">You</p>
                    <p className="text-sm text-gray-600">
                      {myLocation.speed.toFixed(1)} kt · {myLocation.heading.toFixed(0)}°
                    </p>
                  </div>
                </Popup>
              </Marker>
            );
          })()}

          {/* Public boaters - CLUSTERED for performance */}
          {showPublic && !reportMode && publicBoaters.length > 0 && (
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={60}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              iconCreateFunction={createClusterIcon}
              disableClusteringAtZoom={16}
            >
              {publicBoaters.map((boater, index) => {
                // Check if this boater's location matches a nearby SOS (within ~100m)
                const hasActiveSOS = nearbyPublicSOS.some(sos => {
                  const latDiff = Math.abs(sos.lat - boater.lat);
                  const lonDiff = Math.abs(sos.lon - boater.lon);
                  return latDiff < 0.001 && lonDiff < 0.001; // ~100m tolerance
                });
                
                return (
                  <Marker
                    key={`public-${index}-${boater.lat}-${boater.lon}`}
                    position={[boater.lat, boater.lon]}
                    icon={(!hasActiveSOS && boater.user_id && boatPhotos[boater.user_id])
                      ? createPhotoBoatIcon(boatPhotos[boater.user_id], '#64748b', boater.heading, false, false)
                      : createAnonymousDot(hasActiveSOS)}
                  >
                    <Popup>
                      <div className="text-center">
                        {hasActiveSOS ? (
                          <p className="text-sm text-red-600 font-bold">🆘 Boater in distress</p>
                        ) : (
                          <p className="text-sm text-gray-600">Boater nearby</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {/* Friends - SMOOTHED individual markers (no clustering) */}
          {showFriends && !reportMode && friendsLocations.map((friend) => {
            // Use smoothed position if available, otherwise use raw position
            const smoothed = smoothedFriendPositions[friend.friend_user_id];
            const position = smoothed 
              ? [smoothed.lat, smoothed.lon]
              : [friend.lat, friend.lon];
            
            // Check if friend has active SOS - turn marker red
            const hasActiveSOS = friendsSOS.some(sos => sos.user_id === friend.friend_user_id);
            const markerColor = hasActiveSOS ? '#ef4444' : '#06b6d4'; // Red if SOS, cyan otherwise

            // Stale = no fresh location in the last 10 min. Show last-known
            // position faded so crew never just disappear from the map.
            const ageMs = friend.updated_at ? (Date.now() - new Date(friend.updated_at).getTime()) : Infinity;
            const isStale = ageMs > 10 * 60 * 1000;
            // Overdue = no signal for the safety threshold (default 4h) -> amber alert.
            const isOverdue = friend.is_overdue || ageMs > 4 * 60 * 60 * 1000;
            const overdueHrs = isOverdue
              ? Math.max(1, Math.floor((friend.minutes_since_update ?? ageMs / 60000) / 60))
              : 0;
            
            return (
              <Marker
                key={`friend-${friend.friend_user_id}`}
                position={position}
                icon={(!hasActiveSOS && boatPhotos[friend.friend_user_id])
                  ? createPhotoBoatIcon(boatPhotos[friend.friend_user_id], markerColor, friend.heading, isStale, false)
                  : createBoatIcon(markerColor, false, friend.heading, !hasActiveSOS && isStale)}
                opacity={hasActiveSOS ? 1 : (isStale ? 0.45 : 1)}
              >
                <Popup>
                  <div className="text-center min-w-[150px]">
                    {hasActiveSOS && (
                      <div className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded mb-2">
                        🆘 SOS ACTIVE
                      </div>
                    )}
                    <p className="font-bold text-cyan-700">{friend.display_name}</p>
                    {isOverdue ? (
                      <Badge variant="outline" className="text-xs border-amber-500 text-amber-700 bg-amber-50">
                        ⚠️ No signal for {overdueHrs}h
                      </Badge>
                    ) : isStale ? (
                      <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                        Last known position
                      </Badge>
                    ) : (
                      <Badge className={`${getMotionState(friend.speed).color} text-white text-xs`}>
                        {getMotionState(friend.speed).label}
                      </Badge>
                    )}
                    {!isStale && (
                      <p className="text-sm text-gray-600 mt-1">
                        {friend.speed.toFixed(1)} kt · {friend.heading.toFixed(0)}°
                      </p>
                    )}
                    {friend.is_on_trip && !isStale && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        On Trip
                      </Badge>
                    )}
                    <p className={`text-xs mt-1 ${isStale ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                      Updated {formatDistanceToNow(new Date(friend.updated_at))} ago
                    </p>
                  </div>
                </Popup>
                <Tooltip
                  permanent
                  direction="bottom"
                  offset={[0, boatPhotos[friend.friend_user_id] ? 22 : 18]}
                  className={`crew-name-label ${isOverdue ? 'crew-name-label-overdue' : isStale ? 'crew-name-label-stale' : ''}`}
                >
                  {isOverdue
                    ? `${friend.display_name} · no signal ${overdueHrs}h`
                    : isStale
                    ? friend.display_name
                    : `${friend.display_name} · ${friend.speed.toFixed(1)} kt`}
                </Tooltip>
              </Marker>
            );
          })}

          {/* Reports */}
          {showReports && !reportMode && nearbyReports.map((report) => (
            <Marker
              key={`report-${report.id}`}
              position={[report.lat, report.lon]}
              icon={createReportIcon(report.type)}
            >
              <Popup>
                <div className="text-center min-w-[180px]">
                  <p className="font-bold text-gray-800">
                    {report.type === 'HAZARD' ? '⚠️ Hazard' : '📍 Activity'}
                  </p>
                  <p className="text-sm font-medium text-gray-700 capitalize">
                    {report.subtype.replace(/_/g, ' ')}
                  </p>
                  {report.notes && (
                    <p className="text-sm text-gray-500 mt-1">&quot;{report.notes}&quot;</p>
                  )}
                  <div className="flex items-center justify-center gap-1 mt-2">
                    <span className="text-xs text-gray-500">Confidence:</span>
                    <div className="flex">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full mx-0.5 ${
                            i <= report.confidence ? 'bg-green-500' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDistanceToNow(new Date(report.created_at))} ago
                  </p>
                  <div className="flex gap-2 mt-2 justify-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => voteOnReport(report.id, 'CONFIRM')}
                    >
                      ✓ Still there
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => voteOnReport(report.id, 'DISMISS')}
                    >
                      ✗ Gone
                    </Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Nearby Public SOS markers */}
          {!reportMode && nearbyPublicSOS.map((sos) => (
            <Marker
              key={`sos-public-${sos.id}`}
              position={[sos.lat, sos.lon]}
              icon={createSOSIcon(false)}
            >
              <Popup>
                <div className="text-center min-w-[180px]">
                  <p className="font-bold text-red-600">🆘 Boater in Distress</p>
                  <Badge className="bg-red-500 mt-1">{sos.distress_label}</Badge>
                  <p className="text-sm text-gray-600 mt-2">
                    {sos.distance_miles} miles away
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {sos.minutes_ago} min ago • {sos.helper_count} helping
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 bg-green-500 hover:bg-green-600 text-xs"
                    onClick={() => acknowledgeSOS(sos.id, 'PUBLIC')}
                  >
                    On my way
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Friends Private SOS markers */}
          {!reportMode && friendsSOS.map((sos) => (
            <Marker
              key={`sos-private-${sos.id}`}
              position={[sos.lat, sos.lon]}
              icon={createSOSIcon(true)}
            >
              <Popup>
                <div className="text-center min-w-[200px]">
                  <p className="font-bold text-cyan-600">🆘 {sos.display_name} needs help!</p>
                  <Badge className="bg-cyan-500 mt-1">{sos.distress_label}</Badge>
                  {sos.message && (
                    <p className="text-sm text-gray-600 mt-2 italic">&quot;{sos.message}&quot;</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {sos.speed?.toFixed(1)} kt • {sos.motion_state || 'Unknown'}
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 bg-cyan-500 hover:bg-cyan-600 text-xs"
                    onClick={() => acknowledgeSOS(sos.id, 'PRIVATE')}
                  >
                    On my way to help
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Emergency Markers (MOB - Man Overboard) */}
          {!reportMode && emergencyMarkers?.map((marker) => (
            <Marker
              key={`mob-${marker.id}`}
              position={[marker.lat, marker.lon]}
              icon={createMOBIcon(marker.marker_type, marker.is_mine)}
            >
              <Popup>
                <div className="text-center min-w-[200px]">
                  <p className="font-bold text-amber-600">
                    {marker.marker_type === 'PERSON_OVERBOARD' && '👤 Man Overboard'}
                    {marker.marker_type === 'DROPPED_ITEM' && '📦 Dropped Item'}
                    {marker.marker_type === 'HAZARD' && '⚠️ Hazard Marked'}
                    {marker.marker_type === 'LAST_KNOWN' && '📍 Last Known Location'}
                  </p>
                  {marker.label && (
                    <p className="text-sm text-gray-600 mt-1">{marker.label}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Marked by {marker.is_mine ? 'you' : marker.user_display_name || 'crew member'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatDistanceToNow(new Date(marker.created_at))} ago
                  </p>
                  {marker.is_mine && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 text-xs"
                      onClick={() => clearEmergencyMarker(marker.id)}
                    >
                      Clear Marker
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="mt-2 ml-2 bg-blue-500 hover:bg-blue-600 text-xs"
                    onClick={() => {
                      setNavDestination({ lat: marker.lat, lon: marker.lon, name: marker.label || 'Emergency Marker' });
                      setShowNavigation(true);
                    }}
                  >
                    Navigate Here
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Long-press to drop a saved point */}
          <SavedPointPlacer enabled={!reportMode} onDropPoint={handleDropPoint} />

          {/* Saved Points (Waypoints) */}
          {!reportMode && savedPoints.map((point) => (
            <Marker
              key={`point-${point.id}`}
              position={[point.lat, point.lon]}
              icon={createSavedPointIcon(point.is_mine)}
            >
              <Popup>
                <div className="text-center min-w-[200px]">
                  <p className="font-bold text-cyan-700">🔖 {point.name}</p>
                  {point.notes && (
                    <p className="text-sm text-gray-600 mt-1">{point.notes}</p>
                  )}
                  {!point.is_mine && (
                    <p className="text-xs text-gray-500 mt-1">Shared by {point.owner_name}</p>
                  )}
                  <Button
                    size="sm"
                    className="mt-2 bg-blue-500 hover:bg-blue-600 text-xs"
                    onClick={() => handleNavigatePoint(point)}
                    data-testid={`map-navigate-point-${point.id}`}
                  >
                    Navigate Here
                  </Button>
                  {point.is_mine && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 ml-2 text-xs text-red-500 hover:text-red-600"
                      onClick={() => handleDeletePoint(point.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Navigation Panel */}
          <NavigationPanel 
            myLocation={myLocation} 
            isActive={showNavigation} 
            onToggle={() => setShowNavigation(false)}
            destination={navDestination}
            setDestination={setNavDestination}
            onRequirePlus={(feature) => { setPremiumFeature(feature); setShowPremiumModal(true); }}
          />

          {/* Marina Panel */}
          <MarinaPanel 
            lat={myLocation?.lat}
            lon={myLocation?.lon}
            isVisible={showMarinas}
            onClose={() => setShowMarinas(false)}
          />
        </MapContainer>
      </div>

      {/* Floating Action Buttons - Bottom Right */}
      {!reportMode && !panelOpen && (
        <div className="absolute bottom-8 right-3 z-[1000] flex flex-col gap-2" style={{ pointerEvents: 'auto' }}>
          {/* Report Hazard Button */}
          <button
            onClick={handleReportModeToggle}
            onTouchEnd={(e) => { e.preventDefault(); handleReportModeToggle(); }}
            className="w-12 h-12 rounded-full bg-orange-500 hover:bg-orange-600 shadow-lg flex items-center justify-center text-white transition-all active:scale-95 cursor-pointer"
            style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
            data-testid="report-btn"
            title="Report Hazard"
          >
            <MapPin className="w-5 h-5" />
          </button>
          
          {/* Navigation Toggle */}
          <button
            onClick={() => setShowNavigation(!showNavigation)}
            onTouchEnd={(e) => { e.preventDefault(); setShowNavigation(!showNavigation); }}
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95 cursor-pointer ${
              showNavigation ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
            data-testid="navigation-btn"
            title="Navigation"
          >
            <Navigation className="w-5 h-5" />
          </button>
          
          {/* Locate Me Button - with auto-follow indicator */}
          <button
            onClick={handleCenterOnMe}
            onTouchEnd={(e) => { e.preventDefault(); handleCenterOnMe(); }}
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95 cursor-pointer ${
              autoFollow 
                ? 'bg-cyan-500 text-white ring-2 ring-cyan-300 ring-offset-2' 
                : myLocation 
                  ? 'bg-white text-cyan-600 hover:bg-gray-50' 
                  : 'bg-gray-200 text-gray-400 animate-pulse'
            }`}
            style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
            data-testid="center-on-me"
            title={autoFollow ? "Auto-follow ON (tap to disable)" : "Center on my location (tap to enable follow)"}
          >
            <Target className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Trip Status Indicator - Small floating badge when on trip */}
      {activeTrip && !reportMode && (
        <div className="absolute bottom-8 left-3 z-[1000]">
          <div className="bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span>Trip Active</span>
            {myLocation && <span className="opacity-75">• {myLocation.speed.toFixed(1)} kt</span>}
          </div>
        </div>
      )}

      {/* First-Time User Onboarding Overlay */}
      {showOnboarding && (
        <div className="absolute inset-0 z-[2000] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 text-white text-center">
              <h2 className="text-xl font-bold">Welcome to Boat 360!</h2>
              <p className="text-cyan-100 text-sm mt-1">Quick guide to get you started</p>
            </div>
            
            {/* Instructions */}
            <div className="p-4 space-y-4">
              {/* Menu */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Menu className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Menu</p>
                  <p className="text-sm text-gray-500">Map layers, overlays & settings</p>
                </div>
              </div>
              
              {/* SOS */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">SOS</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Emergency SOS</p>
                  <p className="text-sm text-gray-500">Alert nearby boaters in an emergency</p>
                </div>
              </div>
              
              {/* Report */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Report Hazard</p>
                  <p className="text-sm text-gray-500">Mark hazards, debris, or activity</p>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Navigation</p>
                  <p className="text-sm text-gray-500">Get bearing & distance to destination</p>
                </div>
              </div>
              
              {/* Locate */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Locate Me</p>
                  <p className="text-sm text-gray-500">Center map on your position</p>
                </div>
              </div>
              
              {/* Bottom Nav */}
              <div className="flex items-start gap-3 pt-2 border-t">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-lg">↑</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Swipe Up</p>
                  <p className="text-sm text-gray-500">Access Crew, Trips, Reports & Settings</p>
                </div>
              </div>
            </div>
            
            {/* Dismiss Button */}
            <div className="p-4 bg-gray-50">
              <button
                onClick={dismissOnboarding}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-lg transition-colors"
                data-testid="dismiss-onboarding"
              >
                Got it, let&apos;s go!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapScreen;
